import { useState, useEffect, useContext, useMemo } from "react";
import placeholder from "../inc/placeholder.jpg";
import { SiteContext } from "../contexts/SiteContext";
import fetchSite from "../utils/fetchSite";
import { useParams, Link } from "react-router-dom";
import {
	Paper,
	Typography,
	makeStyles,
	Fade,
	Button,
	useTheme,
} from "@material-ui/core";
import LayersIcon from "@material-ui/icons/Layers";
import Loading from "../components/Loading";
import Header from "../components/Header";
import AppBar from "../components/AppBar";
import Breadcrumbs from "../components/Breadcrumbs";
import AdminBar from "../components/AdminBar";
import Banner from "../components/Banner";

function Units() {
	const [loading, setLoading] = useState(true);
	const [units, setUnits] = useState();
	const { siteSlug } = useParams();
	const { selectedSite, setSelectedSite } = useContext(SiteContext);
	const theme = useTheme();

	const useStyles = makeStyles((theme) => ({
		root: {
			flex: 1,
			paddingBottom: "4rem",
		},
		main: {
			margin: "0 auto",
			padding: "1rem",
			paddingTop: 0,
			maxWidth: process.env.REACT_APP_MAX_WIDTH,
		},
		unit: {
			padding: "1rem",
			marginBottom: "1rem",
			display: "flex",
			[theme.breakpoints.down("xs")]: {
				flexDirection: "column",
			},
		},
		unitImage: {
			borderRadius: "5px",
			height: "250px",
			width: "250px",
			minWidth: "250px",
			[theme.breakpoints.down("xs")]: {
				width: "100%",
				minWidth: "100%",
				paddingBottom: "100%",
			},
		},
		unitInfo: {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			padding: "1rem",
			height: '100%'
		},
		unitDescription: {
		},
		unitTitle: {
			textDecoration: "none",
			marginBottom: '1rem'
		},
		readMore: {
			marginTop: "1rem",
			width: 'fit-content'
		},
	}));

	const classes = useStyles();

	// This effect sets the selectedSite based on the :siteSlug (first url parameter)
	useEffect(() => {
		fetchSite(siteSlug, setSelectedSite);
	}, [siteSlug, setSelectedSite]);

	// This effect sets the units and uses the selectedSite._id to make sure it's on the right site
	useEffect(() => {
		async function fetchUnits() {
			let res = await fetch(
				`${process.env.REACT_APP_API_URL}/units?&site=${selectedSite._id}&sort=sortDate`,
				{ credentials: "include" }
			);
			res = await res.json();
			if (res.success) {
				const unitsNoChildren = res.data.filter(
					(unit) => unit.parent === null || unit.parent === undefined
				);
				setUnits(unitsNoChildren);
				setLoading(false);
			} else {
				console.error(res.error);
			}
		}
		if (selectedSite) {
			fetchUnits();
		}
	}, [selectedSite]);

	if (loading || !selectedSite || !units) {
		return <Loading />;
	}

	return (
		<Fade in={typeof units != "undefined"}>
			<div className={classes.root}>
				<AdminBar />
				<Header />
				<AppBar />
				<Banner />
				<Breadcrumbs
					links={[
						{
							name: "Units",
							url: `/${selectedSite.slug}/units`,
						},
					]}
				/>
				<main className={classes.main}>
					<Typography
						className={classes.title}
						gutterBottom
						component="h2"
						variant="h6"
					>
						<LayersIcon /> All Units
					</Typography>
					{units.map((unit, index) => (
						<Paper className={classes.unit}>
							{unit.featuredImage ? (
								<div
									key={index}
									className={classes.unitImage}
									style={{
										background: `url('${process.env.REACT_APP_FILE_URL}/${unit.featuredImage.thumbnailName}')`,
										backgroundSize: "cover",
										backgroundPosition: "center center",
									}}
								/>
							) : (
								<div
									key={index}
									className={classes.unitImage}
									style={{
										background: `url(${placeholder})`,
										backgroundSize: "cover",
										backgroundPosition: "center center",
									}}
								/>
							)}
							<div>
								<div className={classes.unitInfo}>
									<Link
										className={classes.unitTitle}
										to={`/${selectedSite.slug}/units/${unit.slug}`}
									>
										<Typography color="primary" component="h2" variant="h5">
											{unit.name}
										</Typography>
									</Link>
									<Typography
										className={classes.unitDescription}
										dangerouslySetInnerHTML={{ __html: unit.shortDescription }}
									></Typography>
									<Button
										variant="contained"
										component={Link}
										to={`/${selectedSite.slug}/units/${unit.slug}`}
										className={classes.readMore}
										style={{
											color: "white",
											background: selectedSite.primaryColor
												? selectedSite.primaryColor
												: theme.palette.primary.main,
										}}
									>
										Read More
									</Button>
								</div>
							</div>
						</Paper>
					))}
				</main>
			</div>
		</Fade>
	);
}

export default Units;
